import { DateTime } from "luxon";

// ============================================================
// Recherche rapide d'un allocataire — fonctions pures : règles de saisie,
// libellés des suggestions, parties surlignées, message d'état, accès
// (même règle que l'entrée « Rechercher un allocataire » du menu).
// La route GET recipients/recherche-rapide cherche des débuts de nom et de
// prénom (dans les deux ordres, sans casse ni accents), un matricule ou un
// début de NIR, et rend 8 suggestions au plus.
// ============================================================

export const ENDPOINT_RECHERCHE_RAPIDE = "recipients/recherche-rapide";
export const LONGUEUR_MINIMALE = 2;
export const LONGUEUR_MAXIMALE = 60;
// Délai après la dernière frappe : la route est indexée (quelques ms), on
// n'attend que la fin d'un mot tapé d'un trait.
export const DELAI_FRAPPE = 250;
export const CHEMIN_RECHERCHE_COMPLETE = "/all-recipients";

export const nettoyerSaisie = (saisie) => String(saisie || "").replace(/\s+/g, " ").trim().slice(0, LONGUEUR_MAXIMALE);

export const estRecherchable = (saisie) => nettoyerSaisie(saisie).length >= LONGUEUR_MINIMALE;

export const estNumerique = (saisie) => /^[\d ]+$/.test(nettoyerSaisie(saisie));

// Lettres de base, sans casse ni accents : « Bègue » et « BEGUE » se valent.
export const sansAccents = (texte) =>
  String(texte || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/œ/g, "oe")
    .replace(/æ/g, "ae");

const lettresEtChiffres = (texte) => sansAccents(texte).replace(/[^a-z0-9]/g, "");

// Découpe `texte` en parties dont celles qui commencent comme un mot de la
// saisie sont à surligner : [{ texte, surligne }]. Un mot du texte (séparé
// par une espace, un trait d'union ou une apostrophe) est surligné sur la
// longueur du plus long mot saisi qui le commence.
export function segmentsSurlignes(texte, saisie) {
  const valeur = String(texte || "");
  const mots = nettoyerSaisie(saisie)
    .split(" ")
    .map(lettresEtChiffres)
    .filter(Boolean);
  if (!valeur || !mots.length) return valeur ? [{ texte: valeur, surligne: false }] : [];

  const segments = [];
  const ajouter = (morceau, surligne) => {
    if (!morceau) return;
    const dernier = segments[segments.length - 1];
    if (dernier && dernier.surligne === surligne) dernier.texte += morceau;
    else segments.push({ texte: morceau, surligne });
  };

  valeur.split(/([\s'’-]+)/).forEach((partie) => {
    const base = lettresEtChiffres(partie);
    const mot = base && mots.filter((m) => base.startsWith(m)).sort((a, b) => b.length - a.length)[0];
    if (!mot) {
      ajouter(partie, false);
      return;
    }
    // Nombre de caractères d'origine couvrant le mot saisi (accents compris).
    let longueur = 0;
    let couverts = 0;
    while (longueur < partie.length && couverts < mot.length) {
      couverts += lettresEtChiffres(partie[longueur]).length;
      longueur += 1;
    }
    ajouter(partie.slice(0, longueur), true);
    ajouter(partie.slice(longueur), false);
  });
  return segments;
}

export function nomComplet(allocataire = {}) {
  return [String(allocataire.nom || "").toUpperCase(), allocataire.prenom || ""].filter(Boolean).join(" ");
}

const neLe = (civilite) => (civilite === 2 ? "Née le" : civilite === 1 ? "Né le" : "Né(e) le");

// Ce qui distingue deux homonymes : naissance, matricule, commune. Pour une
// saisie en chiffres, le NIR et le matricule d'abord (ce que l'agent a tapé),
// puis la naissance.
export function detailSuggestion(allocataire = {}, { numerique = false } = {}) {
  const dt = allocataire.neLe ? DateTime.fromISO(allocataire.neLe) : null;
  const naissance = dt && dt.isValid ? `${neLe(allocataire.civilite)} ${dt.toFormat("dd/MM/yyyy")}` : "";
  const matricule = allocataire.numeroAllocataire ? `Matr. ${allocataire.numeroAllocataire}` : "";
  const parties = numerique
    ? [allocataire.nir ? `NIR ${allocataire.nir}` : "", matricule, naissance]
    : [naissance, matricule, allocataire.commune || ""];
  return parties.filter(Boolean).join(" · ");
}

const pluriel = (nombre, singulier, plurielTexte) => (nombre > 1 ? plurielTexte : singulier);

// Message d'état lu par les lecteurs d'écran (role="status").
export function messageEtat({ etat, nombre = 0, plusDeResultats = false, saisie = "" }) {
  switch (etat) {
    case "trop-court":
      return `Saisissez au moins ${LONGUEUR_MINIMALE} caractères : nom, prénom, NIR ou matricule.`;
    case "chargement":
      return "Recherche en cours…";
    case "erreur":
      return "La recherche est indisponible pour le moment. Réessayez dans un instant.";
    case "pret":
      // La recherche rapide ne couvre que les allocataires du flux : le rappeler.
      if (!nombre)
        return `Aucun allocataire du flux ne correspond à « ${nettoyerSaisie(saisie)} ». La recherche complète couvre aussi les temporaires, le stock et les EP.`;
      return plusDeResultats
        ? `${nombre} premiers allocataires : précisez la saisie pour les autres.`
        : `${nombre} ${pluriel(nombre, "allocataire trouvé", "allocataires trouvés")}.`;
    default:
      return "";
  }
}

// Filtres de « Rechercher un allocataire » (toutes les collections) pour une
// saisie : chiffres → NIR (13 chiffres et plus) ou matricule, sinon nom puis
// prénom (premier mot, puis le reste).
export function filtresRechercheComplete(saisie) {
  const valeur = nettoyerSaisie(saisie);
  if (!valeur) return null;
  const base = { firstname: "", lastname: "", nir: "", CAFId: "" };
  const chiffres = valeur.replace(/\s/g, "");
  if (/^\d+$/.test(chiffres)) {
    return { filters: { ...base, [chiffres.length >= 13 ? "nir" : "CAFId"]: chiffres } };
  }
  const [nom, ...prenom] = valeur.split(" ");
  return { filters: { ...base, lastname: nom, firstname: prenom.join(" ") } };
}

// Le widget suit l'entrée « Rechercher un allocataire » du menu : visible
// pour les mêmes rôles (items déjà filtrés par visibiliteMenu).
export function peutRechercherAllocataires(itemsVisibles = []) {
  return itemsVisibles.some(
    (item) =>
      item.path === CHEMIN_RECHERCHE_COMPLETE ||
      (Array.isArray(item.items) && item.items.some((sous) => sous.path === CHEMIN_RECHERCHE_COMPLETE))
  );
}

// Raccourci clavier affiché : ⌘ K sur Mac, Ctrl K ailleurs.
export function libelleRaccourci(plateforme = typeof navigator !== "undefined" ? navigator.platform || navigator.userAgent : "") {
  return /mac|iphone|ipad/i.test(plateforme || "") ? "⌘ K" : "Ctrl K";
}

export const estRaccourciRecherche = (evenement) =>
  Boolean(
    (evenement.ctrlKey || evenement.metaKey) &&
      !evenement.altKey &&
      !evenement.shiftKey &&
      String(evenement.key || "").toLowerCase() === "k"
  );
